// «Управление» — the manager's screen (layout agreed with Tim 05.08).
// Four blocks top-to-bottom: all-pools summary tiles, the admins table with
// editable staff rates + live margin, unowned contributors with an assign
// control, and staff management (create / enable / money visibility).
//
// Only mounted for role=manager (main.tsx guards); every endpoint here is
// additionally manager-gated on the worker, so a stale tab degrades to 403s,
// not to data.
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  api,
  type VistaAdminStatsRow as AdminRow,
  type VistaStaffMe as Me,
  type VistaStaffListRes,
  type VistaSummaryRes as Summary,
  type VistaUnassignedRes,
} from '../api';
import type { T } from '../i18n';
import { rateErrorText } from './PoolPage';

type StaffRow = VistaStaffListRes['rows'][number];
type UnassignedRow = VistaUnassignedRes['rows'][number];

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);
// Sign before the currency: −$17.50, not $-17.50 (Tim, 06.08).
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `${cents < 0 ? '−' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;

// No 0/O/1/l/I — these passwords get read aloud and retyped from messengers.
const PWD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
export const genPassword = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(14));
  return Array.from(bytes, (b) => PWD_ALPHABET[b % PWD_ALPHABET.length]).join('');
};

/** The message the manager forwards to a new hire — one copy button, done. */
const credsText = (t: T, email: string, password: string): string =>
  t('mng.creds.text', { email, password });

function CredsPanel({
  t,
  email,
  password,
  onClose,
}: {
  t: T;
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div class="creds">
      <pre>{credsText(t, email, password)}</pre>
      <div class="creds-actions">
        <button
          class="btn-primary"
          onClick={() => {
            navigator.clipboard.writeText(credsText(t, email, password)).then(() => setCopied(true));
          }}
        >
          {copied ? t('mng.creds.copied') : t('mng.creds.copy')}
        </button>
        <button class="btn-ghost" onClick={onClose}>
          {t('mng.creds.hide')}
        </button>
      </div>
      <div class="muted">{t('mng.creds.note')}</div>
    </div>
  );
}

// F7 — search/status filters for the staff table, persisted the same way as
// PoolPage's (Tim: re-picking the filter on every tab switch was the actual
// complaint). Staff has no contributor-style lifecycle (invited/joined/…) —
// the only status axis here is enabled/disabled, so the filter options are
// staff-shaped rather than a copy of STATUS_LABEL.
const STAFF_FILTERS_KEY = 'vista.staffFilters';
type StaffFilters = { q: string; status: string };
const loadStaffFilters = (): StaffFilters => {
  try {
    const raw = localStorage.getItem(STAFF_FILTERS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return {
      q: typeof parsed?.q === 'string' ? parsed.q : '',
      status: typeof parsed?.status === 'string' ? parsed.status : '',
    };
  } catch {
    return { q: '', status: '' };
  }
};

export function ManagePage({ me, t }: { me: Me; t: T }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [admins, setAdmins] = useState<AdminRow[] | null>(null);
  const [unassigned, setUnassigned] = useState<UnassignedRow[] | null>(null);
  const [staff, setStaff] = useState<StaffRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const initialStaffFilters = useMemo(loadStaffFilters, []);
  const [staffQ, setStaffQ] = useState(initialStaffFilters.q);
  const [staffStatus, setStaffStatus] = useState(initialStaffFilters.status);
  useEffect(() => {
    localStorage.setItem(STAFF_FILTERS_KEY, JSON.stringify({ q: staffQ, status: staffStatus }));
  }, [staffQ, staffStatus]);

  const load = () => {
    api.summary().then(setSummary).catch(() => {});
    api.adminStats().then((r) => setAdmins(r.rows)).catch(() => setAdmins([]));
    api.unassigned().then((r) => setUnassigned(r.rows)).catch(() => setUnassigned([]));
    api.staff().then((r) => setStaff(r.rows)).catch(() => setStaff([]));
  };
  useEffect(load, []);

  const fail = (e: unknown) => {
    setErr(rateErrorText(t, e) ?? String(e));
    load();
  };

  // Staff rate: blur/Enter commits, empty clears (which re-arms the cascade
  // gate for that admin — their contributor-rate edits start failing again).
  const saveStaffRate = async (row: AdminRow, raw: string) => {
    const dollars = raw.trim() === '' ? null : Number(raw);
    if (dollars !== null && (!Number.isFinite(dollars) || dollars < 0)) return;
    const rateCents = dollars === null ? null : Math.round(dollars * 100);
    if (rateCents === row.rateCents) return;
    try {
      await api.patchStaff(row.id, { rateCents });
      setErr(null);
      load(); // margin depends on the rate — recompute server-side, not locally
    } catch (e) {
      fail(e);
    }
  };

  const assignTo = async (contributorId: string, userId: string) => {
    if (!userId) return;
    try {
      await api.assign(contributorId, userId);
      setErr(null);
      load();
    } catch (e) {
      fail(e);
    }
  };

  const toggleStaff = async (row: StaffRow, patch: { disabled?: boolean; showBalance?: boolean }) => {
    try {
      await api.patchStaff(row.id, patch);
      setErr(null);
      load();
    } catch (e) {
      fail(e);
    }
  };

  // Password reset: the manager is the only reset path while email is out.
  const [pwdFor, setPwdFor] = useState<string | null>(null);
  const [pwdValue, setPwdValue] = useState('');
  // Freshly set credentials — rendered once with a copy button, never stored.
  const [creds, setCreds] = useState<{ email: string; password: string } | null>(null);
  const savePassword = async (row: StaffRow) => {
    if (pwdValue.length < 8) {
      setErr(t('mng.pwd.tooShort'));
      return;
    }
    try {
      await api.patchStaff(row.id, { password: pwdValue });
      setErr(null);
      setCreds({ email: row.email, password: pwdValue });
      setPwdFor(null);
      setPwdValue('');
    } catch (e) {
      fail(e);
    }
  };

  // Assign targets: anyone active. Admins own pools; a manager can also take
  // people directly (that is how «бесхозные» get triaged before hiring).
  const assignTargets = useMemo(
    () => (staff ?? []).filter((s) => s.disabled === 0 && s.role !== 'viewer'),
    [staff],
  );

  // F7 — staff search/filter, client-side (the roster is a few dozen rows,
  // not worth a server round trip).
  const filteredStaff = useMemo(() => {
    if (!staff) return null;
    const needle = staffQ.trim().toLowerCase();
    return staff.filter(
      (s) =>
        (!needle || s.email.toLowerCase().includes(needle) || s.name.toLowerCase().includes(needle)) &&
        (!staffStatus || (staffStatus === 'disabled' ? s.disabled === 1 : s.disabled === 0)),
    );
  }, [staff, staffQ, staffStatus]);

  return (
    <>
      {summary && (
        <div class="tiles">
          <div class="tile">
            <div class="n">{fmtH(summary.acceptedHours)} {t('pool.hours')}</div>
            <div class="t">{t('pool.tile.accepted')}</div>
          </div>
          <div class="tile">
            <div class="n">{fmtH(summary.pendingHours)} {t('pool.hours')}</div>
            <div class="t">{t('pool.tile.pending')}</div>
          </div>
          <div class="tile">
            <div class="n">{fmtH(summary.rejectedHours)} {t('pool.hours')}</div>
            <div class="t">{t('pool.tile.rejected')}</div>
          </div>
          <div class="tile">
            <div class="n">{fmtMoney(summary.clientCents)}</div>
            <div class="t">{t('pool.tile.revenue')}</div>
          </div>
          <div class="tile">
            <div class="n">{summary.active}/{summary.people}</div>
            <div class="t">{t('pool.tile.filming')}</div>
          </div>
        </div>
      )}
      {err && (
        <div class="strip bad">
          {t('mng.saveFailed')} {err}
        </div>
      )}

      <h2 class="section">{t('mng.admins')}</h2>
      <div class="tablewrap">
        {admins == null ? (
          <div class="empty">{t('mng.loading')}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('mng.th.staff')}</th>
                <th>{t('mng.th.role')}</th>
                <th>{t('mng.th.rate')}</th>
                <th>{t('mng.th.people')}</th>
                <th>{t('mng.th.accepted')}</th>
                <th>{t('mng.th.pendingShort')}</th>
                <th>{t('mng.th.rejected')}</th>
                <th>{t('mng.th.toContributors')}</th>
                <th>{t('mng.th.margin')}</th>
              </tr>
            </thead>
            <tbody>
              {admins
                .filter((a) => !a.disabled || a.poolSize > 0)
                .map((a) => (
                  <tr
                    class="row"
                    key={a.id}
                    onClick={() => (location.hash = `#/?owner=${encodeURIComponent(a.id)}`)}
                  >
                    <td>
                      {a.name}
                      {a.disabled && <span class="status-chip fraud">{t('mng.disabled')}</span>}
                    </td>
                    <td>{t(`mng.role.${a.role}`)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        class="rate-input"
                        type="number"
                        min="0"
                        step="0.5"
                        value={a.rateCents == null ? '' : (a.rateCents / 100).toString()}
                        placeholder="—"
                        onChange={(e) => saveStaffRate(a, (e.target as HTMLInputElement).value)}
                      />
                    </td>
                    <td>
                      {a.poolSize}
                      {a.unratedCount > 0 && (
                        <span class="sub">{t('mng.noRateCount', { n: a.unratedCount })}</span>
                      )}
                    </td>
                    <td>{fmtH(a.acceptedHours)}</td>
                    <td>{a.pendingHours > 0 ? <span class="status-chip pend">{fmtH(a.pendingHours)}</span> : '0'}</td>
                    <td>{fmtH(a.rejectedHours)}</td>
                    <td>{fmtMoney(a.contributorPayoutCents)}</td>
                    {/* «—» here means "no admin rate yet", not "zero margin".
                        Red = pre-validator data where a pool rate exceeds the
                        admin's — fix the rates, the number is honest. */}
                    <td class={a.marginCents != null && a.marginCents < 0 ? 'neg' : ''}>
                      {fmtMoney(a.marginCents)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 class="section">
        {t('mng.unowned')} {unassigned && unassigned.length > 0 ? `(${unassigned.length})` : ''}
      </h2>
      <div class="tablewrap">
        {unassigned == null ? (
          <div class="empty">{t('mng.loading')}</div>
        ) : unassigned.length === 0 ? (
          <div class="empty">{t('mng.allAssigned')}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>{t('mng.th.status')}</th>
                <th>{t('mng.th.accepted')}</th>
                <th>{t('mng.th.assign')}</th>
              </tr>
            </thead>
            <tbody>
              {unassigned.map((u) => (
                <tr key={u.id} class={u.status === 'blocked' ? 'row-blocked' : ''}>
                  <td>
                    {u.email}
                    {u.name && <span class="sub">{u.name}</span>}
                  </td>
                  <td><span class={`status-chip ${u.status}`}>{u.status}</span></td>
                  <td>{fmtH(u.accepted_seconds / 3600)}</td>
                  <td>
                    <select
                      value=""
                      onChange={(e) => assignTo(u.id, (e.target as HTMLSelectElement).value)}
                    >
                      <option value="">{t('mng.pickAdmin')}</option>
                      {assignTargets.map((s) => (
                        <option value={s.id} key={s.id}>
                          {s.name} ({t(`mng.role.${s.role}`)})
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 class="section">{t('mng.staff')}</h2>
      {staff != null && staff.length > 0 && (
        <div class="toolbar filter-bar">
          <input
            type="search"
            placeholder={t('mng.search')}
            value={staffQ}
            onInput={(e) => setStaffQ((e.target as HTMLInputElement).value)}
          />
          <select value={staffStatus} onChange={(e) => setStaffStatus((e.target as HTMLSelectElement).value)}>
            <option value="">{t('mng.filter.all')}</option>
            <option value="enabled">{t('mng.filter.enabled')}</option>
            <option value="disabled">{t('mng.filter.disabled')}</option>
          </select>
        </div>
      )}
      <div class="tablewrap">
        {filteredStaff == null ? (
          <div class="empty">{t('mng.loading')}</div>
        ) : filteredStaff.length === 0 ? (
          <div class="empty">{staff?.length === 0 ? t('mng.noStaff') : t('mng.noMatch')}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>{t('mng.th.name')}</th>
                <th>{t('mng.th.role')}</th>
                <th>{t('mng.th.pool')}</th>
                <th>{t('mng.th.enabled')}</th>
                <th>{t('mng.th.seesMoney')}</th>
                <th>{t('mng.th.password')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map((s) => (
                <tr key={s.id} class={s.disabled === 1 ? 'row-blocked' : ''}>
                  <td>{s.email}</td>
                  <td>{s.name}</td>
                  <td>{t(`mng.role.${s.role}`)}</td>
                  <td>{s.pool_size}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={s.disabled === 0}
                      disabled={s.id === me.id}
                      onChange={(e) =>
                        toggleStaff(s, { disabled: !(e.target as HTMLInputElement).checked })
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={s.show_balance === 1}
                      onChange={(e) =>
                        toggleStaff(s, { showBalance: (e.target as HTMLInputElement).checked })
                      }
                    />
                  </td>
                  <td>
                    {pwdFor === s.id ? (
                      <span class="pwd-edit">
                        <input
                          type="text"
                          class="rate-input pwd-input"
                          placeholder={t('mng.pwd.new')}
                          value={pwdValue}
                          onInput={(e) => setPwdValue((e.target as HTMLInputElement).value)}
                          onKeyDown={(e) => {
                            if ((e as KeyboardEvent).key === 'Enter') savePassword(s);
                            if ((e as KeyboardEvent).key === 'Escape') setPwdFor(null);
                          }}
                        />
                        <button
                          class="link"
                          title={t('mng.pwd.generate')}
                          onClick={() => setPwdValue(genPassword())}
                        >
                          🎲
                        </button>
                        <button class="link" onClick={() => savePassword(s)}>
                          {t('mng.pwd.save')}
                        </button>
                      </span>
                    ) : (
                      <button
                        class="link"
                        onClick={() => {
                          setPwdFor(s.id);
                          setPwdValue('');
                        }}
                      >
                        {t('mng.pwd.change')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {creds && (
        <CredsPanel t={t} email={creds.email} password={creds.password} onClose={() => setCreds(null)} />
      )}
      <CreateStaff t={t} onDone={load} onError={(m) => setErr(m)} onCreated={setCreds} />
    </>
  );
}

// ── create staff ────────────────────────────────────────────────────────────

function CreateStaff({
  t,
  onDone,
  onError,
  onCreated,
}: {
  t: T;
  onDone: () => void;
  onError: (m: string) => void;
  onCreated: (c: { email: string; password: string }) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'viewer' | 'admin' | 'manager'>('admin');
  const [busy, setBusy] = useState(false);

  const valid = email.trim() && name.trim() && password.length >= 8;

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const created = await api.createStaff({ email: email.trim(), name: name.trim(), role, password });
      onCreated({ email: created.email, password });
      setEmail('');
      setName('');
      setPassword('');
      onDone();
    } catch (err) {
      onError(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form class="toolbar" onSubmit={submit}>
      <input
        type="email"
        placeholder={t('mng.new.email')}
        value={email}
        onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
      />
      <input
        type="text"
        placeholder={t('mng.new.name')}
        value={name}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
      />
      <input
        type="text"
        placeholder={t('mng.new.password')}
        value={password}
        onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
      />
      <button
        type="button"
        class="btn-ghost"
        title={t('mng.new.generate')}
        onClick={() => setPassword(genPassword())}
      >
        🎲
      </button>
      <select value={role} onChange={(e) => setRole((e.target as HTMLSelectElement).value as typeof role)}>
        <option value="admin">{t('mng.role.admin')}</option>
        <option value="viewer">{t('mng.role.viewer')}</option>
        <option value="manager">{t('mng.role.manager')}</option>
      </select>
      <button class="btn-primary" disabled={busy || !valid}>
        {busy ? t('mng.new.creating') : t('mng.new.submit')}
      </button>
    </form>
  );
}
